<script setup lang="ts">
import { onBeforeMount, computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { UUID } from '@hfc/common/src/types/UUID';
import { ToastTypes } from '@hfc/ui-core';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import AddPersonButton from '@/quote/infrastructure/components/AddPersonButton.vue';
import { SmallBusinessEmployee } from '@/application/smallBusiness/domain/SmallBusinessEmployee';
import SmallBusinessPersonForm from '@/application/smallBusiness/infrastructure/components/SmallBusinessPersonForm.vue';
import SmallBusinessDependentListForm from '@/application/smallBusiness/infrastructure/components/SmallBusinessDependentListForm.vue';
import { CalendlyEvent } from '@/consent/medicare/domain/CalendlyEventResponse';

interface EmitInterface {
  (event: 'goToSection', value: number): void;
}

const emits = defineEmits<EmitInterface>();

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();
const { appStore } = useAppStore();

const smallBusinessPersonFormRef = ref<Array<InstanceType<typeof SmallBusinessPersonForm>>>([]);
const smallBusinessDependentListFormRef = ref<Array<InstanceType<typeof SmallBusinessDependentListForm>>>([]);

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const employees = computed(() => smallBusinessState.value.application.applicants as Array<SmallBusinessEmployee>);

const showDetailedEmployeeToggle = computed(() => !smallBusinessState.value.application.referenceGroupId);

const updateEmployee = (value: unknown, field: string, id: UUID): void => {
  smallBusinessStore.updateEmployee({ [field]: value, id });
};

onBeforeMount(() => {
  const applicantsToAdd = smallBusinessState.value.companySize - smallBusinessState.value.application.applicants.length;
  if (applicantsToAdd > 0 || smallBusinessState.value.application.applicants.length === 0) {
    smallBusinessStore.addEmployee(applicantsToAdd);
  }
  smallBusinessStore.clearDependentToFocus();
});

async function sectionSubmit (): Promise<boolean> {
  if (smallBusinessState.value.application.applicants.length === 0) {
    appStore.addToast({
      type: ToastTypes.DANGER,
      message: 'Please add at least one employee.'
    });

    return false;
  }
  const localValid = await runValidations();
  const personFormsValid = await Promise.all(smallBusinessPersonFormRef.value.map(form => form.isValid()));
  const dependentsFormsValid = await Promise.all(smallBusinessDependentListFormRef.value.map(section => section.isValid()));
  if (smallBusinessState.value.isSmallCompany) {
    await smallBusinessStore.updateApplication({ appointment: {} as CalendlyEvent });
  }

  return !!(localValid && personFormsValid.every(valid => valid) && dependentsFormsValid.every(valid => valid) && smallBusinessState.value.application.applicants.length > 0);
}

const showSampleEmployee = async (): Promise<void> => {
  emits('goToSection', 1);
  await smallBusinessStore.updateDetailedEmployeesFlag(false);
};

defineExpose({
  sectionSubmit
});
</script>
<template>
  <BaseSection>
    <div class="text-2xl font-semibold text-blue-500 mb-2">Employee Details</div>
    <div v-for="({...applicant}, i) in employees" :key="i">
      <div class="px-4 border border-gray-300 rounded-lg mb-4 bg-slate-50 shadow-md">
        <SmallBusinessPersonForm
          :person="applicant"
          :index="i"
          label="Employee"
          ref="smallBusinessPersonFormRef"
          @updatePerson="updateEmployee"
          @remove="smallBusinessStore.removeEmployee(applicant.id)"
          :show-add-dependents-button="true"
          :show-dependents-count="true"
          :validate-coverage="false"
          :can-remove="employees.length > 2"
        />
        <SmallBusinessDependentListForm :applicant="applicant" :show="applicant.dependents.length > 0" ref="smallBusinessDependentListFormRef" />
      </div>
    </div>
    <div class="flex flex-row justify-between items-center space-x-4">
      <AddPersonButton label="Add Employee" icon="plus" @click="smallBusinessStore.addEmployee()" />
      <div v-if="showDetailedEmployeeToggle" class="flex-1">Click <button class="underline cursor-pointer text-blue-400" @click="showSampleEmployee">here</button> to use sample employee information.</div>
    </div>
  </BaseSection>
</template>
